/* eslint-disable sonarjs/cognitive-complexity -- invalidateEmailCaches enumerates every email-adjacent cache key by design */
import { AuthApiError, FunctionsHttpError } from '@supabase/supabase-js';

import { emailThreadAttachmentKeys } from '@/hooks/use-email-thread-attachments-keys';
import { CorrespondenceApiError } from '@/lib/errors/correspondence-api-error';
import { QK_EMAIL_FILTER_COUNTS, QK_SAVED_VIEW_COUNTS } from '@/hooks/email-cache-keys';
import { invalidateReportingAggregates } from '@/hooks/reporting-aggregate-invalidation';

import type { CorrespondenceErrorCode } from '@/lib/errors/correspondence-api-error';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { QueryClient } from '@tanstack/react-query';

const swallowError = (): undefined => undefined;

/**
 * Thrown by hooks that call edge functions, carrying the upstream HTTP status
 * so retry predicates can branch (e.g., skip retry on 403). Replaces the
 * ad-hoc `Error & { status?: number }` mutation pattern that proliferated
 * across hooks; gives callers a real `instanceof` check instead of duck-typing
 * on a numeric `status` field that any object could happen to have.
 */
export class EdgeFunctionError extends Error {
  readonly status?: number;

  /**
   * Stable slug from the response envelope's `code`, where the function sends
   * one. Several distinct server guards deliberately share a single canonical
   * message, so the slug is the only thing that tells them apart — telemetry
   * that carries the message alone cannot attribute the failure to a layer.
   */
  readonly errorCode?: string;

  constructor(message: string, status?: number, errorCode?: string) {
    super(message);
    this.name = 'EdgeFunctionError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

/**
 * Extract the HTTP status from a `FunctionsHttpError`. The SDK exposes the
 * raw `Response` on `.context`; reading the status via `instanceof Response`
 * is the typed contract — `FunctionsError.context` itself is declared `any`
 * upstream, and `FunctionsRelayError`/`FunctionsFetchError` don't carry one.
 */
export function getSupabaseFunctionStatus(error: unknown): number | undefined {
  if (error instanceof FunctionsHttpError && error.context instanceof Response) {
    return error.context.status;
  }
  return undefined;
}

/**
 * Convenience: extract the message + status from a `supabase.functions.invoke`
 * error and wrap them in an `EdgeFunctionError`. Throw the result so a single
 * `instanceof EdgeFunctionError` narrowing covers the message, the status,
 * and the retry-predicate branch — no inline casts at call sites.
 */
export async function toEdgeFunctionError(error: unknown): Promise<EdgeFunctionError> {
  const message = await getSupabaseFunctionError(error);
  const errorCode = await getSupabaseFunctionErrorCode(error);
  return new EdgeFunctionError(message, getSupabaseFunctionStatus(error), errorCode ?? undefined);
}

/**
 * Patterns that indicate an auth/session error (not a permissions error).
 * Stored pre-lowercased to avoid repeated allocations.
 * IMPORTANT: Do NOT add broad patterns like 'authentication' or 'jwt' —
 * they match non-expiry errors (e.g., "two-factor authentication required").
 */
const AUTH_ERROR_PATTERNS = [
  'invalid jwt',
  'jwt expired',
  'invalid refresh token',
  'session_expired',
  'refresh_token_not_found',
  'missing authorization',
  'not authenticated', // also matches "User not authenticated"
  'pgrst301',
  'invalidjwttoken', // Supabase Realtime WebSocket JWT expiry
] as const;

/**
 * Extracts a message string from any error type.
 * Useful for serializing unknown errors in console.error / Sentry.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = (error as { message: unknown }).message;
    return typeof msg === 'string' ? msg : JSON.stringify(msg);
  }
  return typeof error === 'string' ? error : JSON.stringify(error);
}

/**
 * Detects auth/session errors that should NOT be retried.
 * Uses structured error properties first, falls back to message matching.
 * Does NOT match 403/permission errors — those are a different concern.
 */
export function isAuthError(error: unknown): boolean {
  // 1. Check Supabase AuthApiError type (most reliable)
  if (error instanceof AuthApiError && error.status === 401) {
    return true;
  }

  // 2. Check HTTP status code on error objects (handle both number and string)
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const { status } = error as Record<string, unknown>;
    if (status === 401 || status === '401') {
      return true;
    }
  }

  // 3. Fall back to message pattern matching
  const message = getErrorMessage(error);
  const lowerMessage = message.toLowerCase();
  return AUTH_ERROR_PATTERNS.some((pattern) => lowerMessage.includes(pattern));
}

export { isTransientFetchError } from '@/lib/errors/transient-fetch-error';

/**
 * Expected email-account connection states — not application faults. A
 * `CorrespondenceApiError` carrying one of these codes means the user's mailbox
 * link is gone (OAuth token revoked, or the account was never connected); the
 * correct surface is a reconnect CTA, not telemetry. Callers gate their
 * Sentry-bound `console.error` on this the same way they gate transport blips
 * with `isTransientFetchError`. This affects logging only, never UX.
 *
 * Gated on the typed `code` (a stable facade contract), not a message match, so
 * a bare `Error` that happens to stringify `TOKEN_REVOKED` does not slip through.
 */
const EXPECTED_ACCOUNT_STATE_CODES: ReadonlySet<CorrespondenceErrorCode> = new Set([
  'ACCOUNT_NOT_CONNECTED',
  'TOKEN_REVOKED',
]);

export function isExpectedAccountStateError(error: unknown): error is CorrespondenceApiError {
  return error instanceof CorrespondenceApiError && EXPECTED_ACCOUNT_STATE_CODES.has(error.code);
}

/**
 * The handler's machine-readable `code` off an invoke failure, or null.
 *
 * `getSupabaseFunctionError` reads `error`/`message` and never `code`, so a surface that
 * branches on a specific code (rather than showing a message) has to read the body itself.
 * A non-JSON or unreadable body simply has no code to offer.
 */
export async function getSupabaseFunctionErrorCode(error: unknown): Promise<string | null> {
  if (!(error instanceof FunctionsHttpError)) {
    return null;
  }
  try {
    // Clone the response: bodies can only be read once, and the caller usually also
    // passes the same error to `getSupabaseFunctionError`.
    const ctx = error.context as Response | undefined;
    const response = ctx && typeof ctx.clone === 'function' ? ctx.clone() : ctx;
    const bodyText = response ? await response.text() : '';
    if (!bodyText) {
      return null;
    }
    const parsed = JSON.parse(bodyText) as { code?: unknown };
    return typeof parsed.code === 'string' ? parsed.code : null;
  } catch {
    return null;
  }
}

/**
 * Extracts error message from Supabase function response
 * Handles FunctionsHttpError by reading the error context
 *
 * @param error - The error from supabase.functions.invoke response
 * @returns Promise<string> - The error message
 */
export async function getSupabaseFunctionError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      // Clone the response to avoid body stream consumption issues
      // Response bodies can only be read once, so cloning ensures we can read it
      const ctx = error.context as Response | undefined;
      const response = ctx && typeof ctx.clone === 'function' ? ctx.clone() : ctx;
      const bodyText = response ? await response.text() : '';
      if (bodyText) {
        try {
          const errorData = JSON.parse(bodyText) as { error?: string; message?: string };
          return errorData.error || errorData.message || error.message;
        } catch {
          // Response body wasn't JSON, return as-is if it looks like an error message
          if (bodyText.length < 500 && !bodyText.includes('<')) {
            return bodyText;
          }
        }
      }
    } catch {
      // If reading body fails, fall back to error message
    }
    return error.message;
  }

  // Fallback for other error types
  if (error && typeof error === 'object' && 'message' in error) {
    const { message: msg } = error;
    if (typeof msg === 'string' && msg) {
      return msg;
    }
  }
  return 'An unexpected error occurred';
}

/**
 * Extracts user-friendly error message from Supabase database errors
 * Handles PostgrestError and generic errors from database operations
 *
 * @param error - The error from supabase database operations
 * @returns string - A user-friendly error message
 */
export function getSupabaseDatabaseError(error: unknown): string {
  // Handle PostgrestError
  if (error && typeof error === 'object') {
    // Check for common Supabase error codes
    const errObj = error as { code?: string; message?: string; details?: string; hint?: string };
    const { code } = errObj;
    const message = errObj.message ?? '';
    const details = errObj.details ?? '';
    const hint = errObj.hint ?? '';

    // Map common error codes to user-friendly messages
    switch (code) {
      case 'PGRST116':
        return 'No matching record found or you do not have permission to access it';
      case '23505': // unique_violation
        return 'This record already exists';
      case '23503': // foreign_key_violation
        return 'Cannot complete operation: referenced record does not exist';
      case '23502': // not_null_violation
        return 'Required field is missing';
      case '42501': // insufficient_privilege
        return 'You do not have permission to perform this action';
      case '42P01': // undefined_table
        return 'Database configuration error: table not found';
      case 'P0001': // raise_exception (custom error from database)
        return message || 'Operation failed';
      default:
        // For unknown codes, try to provide helpful context
        if (message) {
          // Clean up technical jargon from error messages
          const cleanMessage = message
            .replace(/^new row for relation ".*?" violates.*?constraint/, 'Validation failed:')
            .replace(
              /^duplicate key value violates unique constraint/,
              'This record already exists',
            )
            .replace(
              /^insert or update on table ".*?" violates foreign key constraint/,
              'Referenced record not found',
            )
            .replace(
              /^null value in column ".*?" violates not-null constraint/,
              'Required field is missing',
            );

          // Add hint if available and helpful
          if (hint && hint !== message) {
            return `${cleanMessage}. ${hint}`;
          }

          return cleanMessage;
        }

        // Last resort: combine available information
        return (
          [message, details, hint].filter(Boolean).join('. ') ||
          'An unexpected database error occurred'
        );
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred';
}

/**
 * Invalidates all email-related caches after an email action
 * This ensures consistency across all views and components
 *
 * Use this after actions like:
 * - Archive/Unarchive
 * - Delete
 * - Mark as read/unread
 * - Update metadata (category, action, notes)
 * - Snooze/Unsnooze
 *
 * @param queryClient - TanStack Query client instance
 * @param organizationId - Current organization ID
 * @param emailId - Optional specific email ID for targeted invalidation
 */
export function invalidateEmailCaches(
  queryClient: QueryClient,
  organizationId: string | undefined,
  emailId?: string,
): void {
  // Fire-and-forget invalidations: TanStack returns a Promise that resolves
  // once subscribed queries refetch. Callers don't await — keep the cluster
  // tidy by routing every call through `inv`, which swallows rejections so
  // a single failing refetch can't poison the rest.
  const inv = (opts: Parameters<QueryClient['invalidateQueries']>[0]): void => {
    queryClient.invalidateQueries(opts).catch(swallowError);
  };
  // Invalidate provider-specific message caches (legacy)
  inv({ queryKey: ['gmail', 'messages', organizationId] });
  inv({ queryKey: ['outlook', 'messages', organizationId] });
  inv({ queryKey: ['imap', 'messages', organizationId] });

  // Invalidate email metadata caches
  inv({ queryKey: ['email-metadata'] });
  inv({ queryKey: ['email-metadata-batch'] });

  // Invalidate thread caches (used by useEmailThreadProgressive)
  // These are stored at [provider, 'thread', threadId, organizationId]
  // We invalidate broadly since we often don't have the specific threadId
  inv({ queryKey: ['gmail', 'thread'] });
  inv({ queryKey: ['outlook', 'thread'] });
  inv({ queryKey: ['imap', 'thread'] });

  // Invalidate per-message caches (used by progressive thread loading)
  // These are stored at [provider, 'message', messageId, organizationId]
  inv({ queryKey: ['gmail', 'message'] });
  inv({ queryKey: ['outlook', 'message'] });
  inv({ queryKey: ['imap', 'message'] });

  // Invalidate thread-attachments queries so the optimistic send-time
  // placeholder is replaced by the canonical email_attachments row once
  // the provider sync lands.
  inv({ queryKey: emailThreadAttachmentKeys.all });

  // Invalidate email index queries (database-driven email list)
  if (organizationId) {
    inv({ queryKey: ['email-index', organizationId] });
  } else {
    inv({ queryKey: ['email-index'] });
  }

  // Invalidate email list function queries (edge function email list)
  if (organizationId) {
    inv({ queryKey: ['email-list-function', organizationId] });
  } else {
    inv({ queryKey: ['email-list-function'] });
  }

  // Invalidate the counter surfaces (sidebar bucket counters + saved-view
  // badges) together. Counters are server-computed and refreshed only by
  // invalidation — never patched arithmetically on the client — so every
  // email mutation that settles through this helper keeps all badge
  // surfaces honest by construction. See docs/adr/0067. Mirror the
  // email-index/email-list-function guard above: `[root, undefined]` would
  // match no org-scoped entry, so fall back to the root prefix when the org
  // is unknown rather than silently skipping the counter refresh.
  if (organizationId) {
    inv({ queryKey: [QK_EMAIL_FILTER_COUNTS, organizationId] });
    inv({ queryKey: [QK_SAVED_VIEW_COUNTS, organizationId] });
  } else {
    inv({ queryKey: [QK_EMAIL_FILTER_COUNTS] });
    inv({ queryKey: [QK_SAVED_VIEW_COUNTS] });
  }

  // Invalidate snoozed emails list. Mirror the org guard above: the query is
  // only enabled with a real org, so `['snoozed-emails', undefined]` would
  // match no live entry — fall back to the root prefix when the org is unknown.
  if (organizationId) {
    inv({ queryKey: ['snoozed-emails', organizationId] });
  } else {
    inv({ queryKey: ['snoozed-emails'] });
  }

  // Invalidate draft caches (drafts are associated with emails)
  inv({ queryKey: ['email-draft'] });
  inv({ queryKey: ['email-drafts-thread'] });

  // Invalidate draft approval queries
  inv({ queryKey: ['draft-approvals'] });

  // Invalidate comment caches (targeted when emailId provided, broad otherwise)
  if (emailId) {
    // Only invalidate comments for this specific email
    inv({ queryKey: ['comments', 'list', 'email', emailId] });
  } else {
    // Broad invalidation for backwards compatibility
    inv({ queryKey: ['comments'] });
  }

  // Invalidate assignment caches (targeted when emailId provided, broad otherwise)
  if (emailId) {
    // Only invalidate assignments for this specific email
    inv({ queryKey: ['email-assignments', emailId] });
  } else {
    // Broad invalidation for backwards compatibility
    inv({ queryKey: ['email-assignments'] });
  }

  // Invalidate the user-keyed assigned-emails lists (Assigned Emails views).
  // Keyed by `['assigned-emails', userId, organizationId]` — the userId is
  // not available here, so scope by org via predicate.
  if (organizationId) {
    inv({
      predicate: (query) =>
        query.queryKey[0] === 'assigned-emails' && query.queryKey.includes(organizationId),
    });
  } else {
    inv({ queryKey: ['assigned-emails'] });
  }

  // Invalidate email analytics
  if (organizationId) {
    inv({ queryKey: ['email-analytics', organizationId] });
  } else {
    inv({ queryKey: ['email-analytics'] });
  }

  // Reporting dashboard tiles aggregate these same rows server-side
  // (correspondences / email_drafts widget data sources) — mark them stale so
  // a revealed Analytics tab refetches instead of serving pre-mutation counts.
  // See .claude/rules/frontend.md → Server-Side Aggregates.
  invalidateReportingAggregates(queryClient, organizationId);
}

/**
 * Expands a list of email IDs to include all emails in their threads.
 *
 * Gmail operates at the thread level, so when archiving/deleting/marking emails,
 * we need to expand the selection to include all emails in the affected threads.
 *
 * This function efficiently handles batch operations by:
 * 1. Fetching thread_ids for ALL selected emails in a single query
 * 2. Getting all email_ids from ALL threads in a single query
 * 3. Deduplicating the results
 *
 * @param supabase - Supabase client instance
 * @param emailIds - Array of email IDs to expand
 * @param organizationId - Current organization ID
 * @param accountId - Optional account scope. When provided, expansion is
 *   restricted to rows of that account. The account — not the org — is the
 *   write-authorization boundary (ADR 0009 / correspondence-api-transition.md
 *   rule #13), so write callers MUST pass it: two accounts in one org can share
 *   a `thread_id` (Outlook conversationId collision), and an org-only expansion
 *   would pull the sibling account's rows into a thread-level write.
 * @returns Array of all email IDs including thread members, deduplicated
 */
export async function expandEmailIdsToThreads(
  supabase: SupabaseClient,
  emailIds: string[],
  organizationId: string,
  accountId?: string | null,
): Promise<string[]> {
  if (emailIds.length === 0) {
    return emailIds;
  }

  try {
    // Get thread_ids for ALL selected emails in a single query.
    // `SupabaseClient` here is untyped (no `<Database>` generic), so PostgREST
    // results widen to `any[]`. Narrow at the destructure to keep the rest of
    // the function type-safe without dragging the full Database type into this
    // utility file.
    let selectedQuery = supabase
      .from('email_metadata')
      .select('thread_id')
      .in('email_id', emailIds)
      .eq('organization_id', organizationId);
    if (accountId) {
      selectedQuery = selectedQuery.eq('account_id', accountId);
    }
    const { data: selectedEmails, error: selectedError } = (await selectedQuery) as {
      data: { thread_id: string | null }[] | null;
      error: PostgrestError | null;
    };

    // PostgREST returns errors in-band ({ data, error }) without throwing.
    // Surfacing it through the catch keeps a failed lookup from silently
    // returning unexpanded IDs, which would reintroduce single-row thread writes.
    if (selectedError) {
      throw selectedError;
    }

    if (!selectedEmails || selectedEmails.length === 0) {
      return emailIds;
    }

    // Get unique thread IDs (filter out nulls)
    const threadIds = [
      ...new Set(selectedEmails.map((e) => e.thread_id).filter((id): id is string => Boolean(id))),
    ];

    if (threadIds.length === 0) {
      return emailIds;
    }

    // Get all email_ids from ALL threads in a single query, scoped to the same
    // account when provided so a shared thread_id can't pull a sibling account's
    // rows into the caller's write.
    let threadQuery = supabase
      .from('email_metadata')
      .select('email_id')
      .in('thread_id', threadIds)
      .eq('organization_id', organizationId);
    if (accountId) {
      threadQuery = threadQuery.eq('account_id', accountId);
    }
    const { data: threadEmails, error: threadError } = (await threadQuery) as {
      data: { email_id: string }[] | null;
      error: PostgrestError | null;
    };

    if (threadError) {
      throw threadError;
    }

    if (!threadEmails || threadEmails.length === 0) {
      return emailIds;
    }

    // Deduplicate in case of overlapping threads
    return [...new Set(threadEmails.map((e) => e.email_id))];
  } catch (error) {
    console.error('Failed to expand email IDs to threads:', error);
    // Return original IDs on error to allow operation to continue
    return emailIds;
  }
}
